import {customElement, property, state} from "lit/decorators.js";
import {LitElement, html, CSSResult, unsafeCSS} from "lit";
import {provide} from '@lit/context';
import {
  OnlineReservationConfig,
  Messages,
  Article,
  Stores
} from "../../@types/online-reservation.js";

import '../tooltip/tooltip.js';

import styles from './online-reservation.scss';
import {OpenHvaModalEvent} from "../../@types/dom.js";
import {EntsDrawer} from "../drawer/drawer.js";
import {LoadingState} from "./loading-state.js";

import './step-one';
import './step-two';
import './step-three';

import { i18nContext } from "./i18nContext.js";



@customElement('ents-online-reservation')
export class OnlineReservation extends LitElement {
  private static readonly EMPTY_MODEL = {
    article: {
      brand: '',
      name: '',
      color: '',
      price: {
        value: '',
        priceReduced: false,
      },
      image: {
        src: '',
        alt: '',
      },
    },
    stores: {
    },
    sizesWithStoreStock: {

    }
  };

  @property({type: Object, reflect: false}) config: OnlineReservationConfig = {
    articleDetailsUrl: '',
    messages: {}
  };

  @provide({context: i18nContext})
  private messages!: Messages;

  @state() private article: Article = OnlineReservation.EMPTY_MODEL.article;

  @state() private stores: Stores = { name: '', storeId: 0, sizesInStore: [] };

  @state() private sizesWithStoreStock: string[] =[];

  @state() private loadingState = LoadingState.loading();

  @state() private currentStep = 0;

  @state() private selectedSize = '';

  constructor() {
    super();
    this.messages = this.config.messages;
  }

  private nextStep(): void {
    if (this.currentStep < 2) this.currentStep++;
  }

  private prevStep(): void {
    if (this.currentStep > 0) this.currentStep--;
  }

  private selectSize(sizeId: string): void {
    this.selectedSize = sizeId;
  }

  static get styles(): CSSResult {
    return unsafeCSS(styles);
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(
      'ents:hva-modal:open', event => this.handleOpenEvent(event));
    this.messages = this.config.messages;
  }

  private handleOpenEvent(event: OpenHvaModalEvent) {
    this.loadModel(event.detail.articleId);
    this.showDrawer();
  }

  private loadModel(variantId: string) {
    const articleDetailsUls = this.config.articleDetailsUrl.replace('VARIANT_ID', variantId);
    fetch(articleDetailsUls)
      .then(response => response.json())
      .then((response) => {
        console.log(response);
        const article = response.article;
        const price = article.price;
        const image = article.image;
        const imageAltText = `${article.brand} ${article.name}${ article.color ?  ': ' +article.color : ''}`;

        this.article = {
            brand: article.brand,
            name: article.name,
            color: article.color,
            price: {
              value: price.value,
              priceReduced: price.priceReduced,
            },
            image: {
              src: image.src,
              alt: imageAltText,
            },
        };

        this.stores = response.stores;

        this.sizesWithStoreStock = response.sizesWithStoreStock;

        this.loadingState = LoadingState.loaded();
      })
      .catch(() => {
        this.loadingState = LoadingState.loadedWithError();
      });
  }

  render() {
    return html`
      <ents-drawer id="onlineReservationDrawer" class="ents-or__drawer">
        <div class="ents-or__drawer__content">
          ${this.renderContent()}
        </div>
      </ents-drawer>
    `;
  }

  private renderContent() {
    return this.loadingState.isFailed
      ? this.renderError()
      : this.renderStep();
  }

  private renderError() {
    return html`
      <div class="ents-or__error">
        <p class="ents-copy ents-or__hint ents-or__hint--error">
          ${this.config.messages.error || 'Error loading article information.'}
        </p>
      </div>
    `;
  }

  private renderStep() {
    return html`
      ${this.currentStep === 0
        ? html`<step-one
          .onNext=${() => this.nextStep()} .article=${this.article}></step-one>`
        : ''}
      ${this.currentStep === 1
        ? html`<step-two
          .stores=${this.stores}
          .sizesWithStoreStock=${this.sizesWithStoreStock}
          .onNext=${() => this.nextStep()}
          .onPrev=${() => this.prevStep()}
          .selectSize=${(sizeId: string) => this.selectSize(sizeId)}
        ></step-two>`
        : ''}
      ${this.currentStep === 2
        ? html`<step-three
          .onPrev=${() => this.prevStep()}
        ></step-three>`
        : ''}
    `;
  }

  private showDrawer() {
    this.currentStep = 0;
    return this.doOnDrawer(drawer => drawer.show());
  }

  private doOnDrawer(action: (modal: EntsDrawer) => void) {
    const drawer = this.shadowRoot?.querySelector<EntsDrawer>('#onlineReservationDrawer');
    if (drawer) {
      action(drawer);
      return true;
    }
    return false;
  }


}


